import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { isEmpty, cloneDeep } from 'lodash';
import type { BodyScrollEvent, GridApi, GridOptions, GridReadyEvent, IRowNode, SelectionChangedEvent } from 'ag-grid-community';
import BombiixAGGrid from '../../../../Components/BombiixAGGrid';
import type { BombiixRowId } from '../../../../Components/BombiixAGGrid';
import { Floaty, FloatyButton } from '../../../../Components/Floaty';
import { DownloadIcon } from '../../../../bombiix-icon';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { getCostingList, filterSortData, setCostingSelectedIds } from './store/Costing.action';
import { Page_Size } from '../../../../Components/helper';
import { fetchColorLibrary } from '../../../Library/ColorLibary/store/ColorLibrary.action';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { useCostingTodoColumns } from './useCostingTodoColumns';
import { useCostingTodoOptions } from './costing-todo-options';
import { buildCostingHeaderStats, readSelectedColumnOptions, resolveCostingInfoLabel } from './costing-todo.helpers';
import { useCostingSave } from './useCostingSave';
import { applyCalcProjection, applyLocalInput, buildCostingEdit, buildFillEdits } from './costing-todo-editing';
import type { CostingCalcProjection } from './costing-todo-editing';
import { downloadCostingTodoExcelSheet } from './costing-todo-excel';
import type {
  CostingDispatch,
  CostingGridRow,
  CostingInfoField,
  CostingListParams,
  CostingRecord,
  CostingRootState,
  CostingTODOTableProps,
} from './store/Costing.type';
import './costing-todo-table.css';
import type { BombiixFillBatchItem } from '../../../../Components/BombiixAGGrid/types';

/* Reproduces CommonAGGridTable's prefetch geometry. BombiixAGGrid only installs
   onBodyScrollEnd, which never fires when the loaded rows do not fill the viewport, so the
   table would stall at one page on tall screens. See IMPLEMENTATION_NOTES.md delta A. */
const LOAD_MORE_SCROLL_RATIO = 0.75;
const GRID_ROW_HEIGHT = 40;

/** Costing TODO grid: dynamic template columns, expandable rows, selection and Excel export. */
export function CostingTODOTable({ activeCostingTemplate, isDownloadLoading, getSelectedCount, getSelectedIdsWithDevIds, onCostingsSaved }: CostingTODOTableProps) {
  const {
    CostingTodoListData: {
      averageTotalCal,
      costingListData,
      costingListTotalCount,
      costingSelectedIds,
      costingFilterValues: {
        searchText,
        pageIndex,
        pageSize,
        sortOrder,
        sortPropertyName,
        loading,
        supplier,
        colourway,
        currency,
        incoterms,
        selectedcosting_Status,
        selectedBrand,
        selectedSubBrand,
        selectedSeason,
        selectedYear,
        selectedMonth,
        selectedProductType,
        selectedCat1,
        selectedCat2,
        selectedCat3,
        selectedCat4,
        selectedCompanyUser,
        selectedDesigner,
        selectedCoo,
        productGroupId,
      },
    },
    Users: { logInRights },
    UserDetails: {
      userRole,
      isSupplierLoggedIn,
      userInfo: { userId },
    },
    CostingTemplateData: { costingTemplateList },
    SystemMastersDetails: { subBrandAccess, subBrandList },
  } = useSelector((reduxState: CostingRootState) => reduxState);

  /* Memoised on the SELECTION, not the render. This is read from localStorage on every render, and
     handing the column builder a fresh array each time made its memo useless — every render
     produced new column definitions, AG Grid re-applied them as a column change, and with
     `maintainColumnOrder` defaulting to false that reset the column order and the horizontal
     scroll on any edit or drag. The signature keeps the array identity stable while the chooser's
     selection is unchanged, and still swaps it the moment the user picks different columns. */
  const selectedColumnsSignature = JSON.stringify(readSelectedColumnOptions().map((option) => option?.value as string));
  const selectedColumns = useMemo<string[]>(() => JSON.parse(selectedColumnsSignature) as string[], [selectedColumnsSignature]);
  /* useComponent lives in an untyped .js module; its `module = null, action = null` defaults
     make TypeScript infer those parameters as `null`, which no call site can satisfy. */
  const { CheckActionPermission } = useComponent() as unknown as {
    CheckActionPermission: (rights: unknown, section: string, module: string, actionName: string) => boolean;
  };
  const [expandedRowIndex, setExpandedRowIndex] = useState<Array<number | null>>([]);
  const MasterAddEditAccess = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'products', 'costing', 'delete');
  const dispatch = useDispatch<CostingDispatch>();

  const [initState, setInitState] = useState<{ hasMore: boolean; loading: boolean; totalRecords: number; costingData: CostingRecord[] }>({
    hasMore: true,
    loading: false,
    totalRecords: 0,
    costingData: [],
  });
  /* Intentionally never reset — see IMPLEMENTATION_NOTES.md B1. Resetting it here would
     uncap infinite scroll and is a behaviour change outside this port's scope. */
  const isFetchingNextRef = useRef(false);
  const { t: translate } = useTranslation('common');
  const { t: translateMyTodo } = useTranslation('modules/my-todo');
  const t = translate as unknown as TranslateFn;
  const tMyTodo = translateMyTodo as unknown as TranslateFn;

  const gridApiRef = useRef<GridApi<CostingGridRow> | null>(null);
  const isSyncingSelectionRef = useRef(false);

  useEffect(() => {
    setInitState((inInfinitestate) => ({
      ...inInfinitestate,
      loading,
    }));
  }, [loading]);

  useEffect(() => {
    const callFn = async () => {
      batch(async () => {
        await dispatch(fetchColorLibrary({ loggedInUserId: userId }));
      });
    };
    if (!isEmpty(userId) && !isSupplierLoggedIn) callFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId, isSupplierLoggedIn]);

  useEffect(() => {
    if (Array.isArray(costingListData)) {
      setInitState((inInfinitestate) => ({
        ...inInfinitestate,
        costingData: costingListData,
      }));
    }
  }, [costingListData]);

  useEffect(() => {
    if (costingListTotalCount > (costingListData?.length ?? 0)) {
      setInitState((inInfinitestate) => ({
        ...inInfinitestate,
        hasMore: true,
      }));
    } else
      setInitState((inInfinitestate) => ({
        ...inInfinitestate,
        hasMore: false,
      }));
  }, [costingListData?.length, costingListTotalCount]);

  /** Fetches one page and appends it, keeping the local loading flag in step. */
  const getCostingListData = async (data: CostingListParams, number?: number) => {
    /* The original spreads the request params straight into component state, which leaves
       stray request keys on it. Preserved verbatim; the cast keeps the declared state shape. */
    setInitState(
      (toGetstate) =>
        ({
          ...toGetstate,
          loading: true,
          ...data,
        }) as typeof toGetstate,
    );

    const params: CostingListParams = {
      pageIndex: number || data.pageIndex,
      pageSize: data.pageSize,
      searchText,
      isScrolled: true,
      costingTemplateId: activeCostingTemplate,
      type: 'costing',
      sortOrder,
      sortPropertyName,
      supplier,
      colourway,
      currency,
      incoterms,
      status: Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [],
      brand: selectedBrand,
      season: selectedSeason,
      year: selectedYear,
      month: selectedMonth,
      productType: selectedProductType,
      catOne: selectedCat1,
      catTwo: selectedCat2,
      catThree: selectedCat3,
      catFour: selectedCat4,
      createdBy: selectedCompanyUser,
      designer: selectedDesigner,
      COO: selectedCoo,
      productGroupId,
      ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
    };
    if (isSupplierLoggedIn) params.supplierId = userId;
    await dispatch(getCostingList(params));
    setInitState((inInfinitestate) => ({
      ...inInfinitestate,
      loading: false,
    }));
  };

  /**
   * Loads the next page for the infinite-scroll table. A synchronous ref guards against the
   * scroll handler firing again before the in-flight request resolves.
   */
  const handlePageNextInScroll = async () => {
    if (isFetchingNextRef.current || Number(costingListData?.length || 0) >= Number(costingListTotalCount || 0)) {
      return;
    }
    isFetchingNextRef.current = true;
    let index = pageIndex;
    index += 1;
    const params: CostingListParams = {
      pageIndex: index,
      pageSize,
      searchText,
      totalRecords: costingListTotalCount,
      type: 'costing',
      sortOrder,
      sortPropertyName,
      supplier,
      colourway,
      currency,
      incoterms,
      status: Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [],
      brand: selectedBrand,
      season: selectedSeason,
      year: selectedYear,
      month: selectedMonth,
      productType: selectedProductType,
      catOne: selectedCat1,
      catTwo: selectedCat2,
      catThree: selectedCat3,
      catFour: selectedCat4,
      createdBy: selectedCompanyUser,
      designer: selectedDesigner,
      COO: selectedCoo,
      productGroupId,
      ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
      costingTemplateId: activeCostingTemplate,
    };
    const obj = {
      pageIndex: index,
      pageSize,
    };
    await dispatch(filterSortData(obj));
    getCostingListData(params);
  };

  /** Server-side sort. The direction toggle is global rather than per column (see B10). */
  const onSortColumn = async (propertyName: string) => {
    if (sortOrder === '' || sortOrder === 'Desc') {
      const sortObj: CostingListParams = {
        sortOrder: 'Asc',
        sortPropertyName: propertyName,
        searchText,
        pageIndex: 1,
        pageSize: Page_Size,
        costingTemplateId: activeCostingTemplate,
        type: 'costing',
        supplier,
        colourway,
        currency,
        incoterms,
        status: Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [],
        brand: selectedBrand,
        season: selectedSeason,
        year: selectedYear,
        month: selectedMonth,
        productType: selectedProductType,
        catOne: selectedCat1,
        catTwo: selectedCat2,
        catThree: selectedCat3,
        catFour: selectedCat4,
        createdBy: selectedCompanyUser,
        designer: selectedDesigner,
        COO: selectedCoo,
        productGroupId,
        ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
      };
      if (isSupplierLoggedIn) sortObj.supplierId = userId;

      const obj = {
        sortOrder: 'Asc',
        sortPropertyName: propertyName,
      };
      await dispatch(filterSortData(obj));
      await dispatch(getCostingList(sortObj));
    } else {
      const sortObj: CostingListParams = {
        sortOrder: 'Desc',
        sortPropertyName: propertyName,
        searchText,
        pageIndex: 1,
        pageSize: Page_Size,
        costingTemplateId: activeCostingTemplate,
        type: 'costing',
        supplier,
        colourway,
        currency,
        incoterms,
        status: Array.isArray(selectedcosting_Status) ? selectedcosting_Status : selectedcosting_Status ? [selectedcosting_Status] : [],
        brand: selectedBrand,
        season: selectedSeason,
        year: selectedYear,
        month: selectedMonth,
        productType: selectedProductType,
        catOne: selectedCat1,
        catTwo: selectedCat2,
        catThree: selectedCat3,
        catFour: selectedCat4,
        createdBy: selectedCompanyUser,
        designer: selectedDesigner,
        COO: selectedCoo,
        productGroupId,
        ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
      };
      if (isSupplierLoggedIn) sortObj.supplierId = userId;
      await dispatch(getCostingList(sortObj));
      const obj = {
        sortOrder: 'Desc',
        sortPropertyName: propertyName,
      };
      await dispatch(filterSortData(obj));
    }
  };

  const isSupplier = userRole === 'Supplier';

  const activeCostingTemplateDetailes = useMemo(() => {
    return costingTemplateList?.find((item) => item?._id === activeCostingTemplate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costingTemplateList?.length, activeCostingTemplate]);

  /** Opens the costing detail screen for a row in a new tab. */
  const handleEdit = (item: CostingRecord) => {
    window.open(`${window.location.origin}/development-order/${item?.devOrderId}/product-costing-detail/${item?._id}`, '_blank');
  };

  const expandMoreData = (index: number) => {
    if (expandedRowIndex.includes(index)) {
      setExpandedRowIndex(expandedRowIndex.filter((item) => item !== index));
    } else {
      setExpandedRowIndex([...expandedRowIndex, index]);
    }
  };

  const topColumnsArr = useMemo(() => {
    if (activeCostingTemplateDetailes?.topColumnArr?.length) {
      return activeCostingTemplateDetailes?.topColumnArr?.filter((item) => {
        return item?.isActive && selectedColumns?.includes(item?.aliasName);
      });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCostingTemplateDetailes?.topColumnArr, selectedColumns?.length]);

  const defaultColumnsArr = useMemo(() => {
    if (activeCostingTemplateDetailes?.defaultColumns?.length) {
      return activeCostingTemplateDetailes?.defaultColumns?.filter((item) => {
        return item?.isActive && selectedColumns?.includes(item?.aliasName);
      });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCostingTemplateDetailes?.defaultColumns, selectedColumns?.length]);

  const bottomColumnsArr = useMemo(() => {
    if (activeCostingTemplateDetailes?.bottomColumnArr?.length) {
      return activeCostingTemplateDetailes?.bottomColumnArr?.filter((item) => {
        return item?.isActive && selectedColumns?.includes(item?.aliasName);
      });
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCostingTemplateDetailes?.bottomColumnArr, selectedColumns?.length]);

  const isCostingInfoActive = activeCostingTemplateDetailes?.isCostingInfoActive ?? false;

  const defaultCostingInfo = useMemo(() => (activeCostingTemplateDetailes?.costingInfo ?? []).filter((ci) => ci?.isDefault), [activeCostingTemplateDetailes]);

  const dynamicCostingInfo = useMemo(
    () => (activeCostingTemplateDetailes?.costingInfo ?? []).filter((ci) => !ci?.isDefault).sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0)),
    [activeCostingTemplateDetailes],
  );

  /**
   * Resolves a default column's label, honouring the template's costingInfo alias overrides.
   * `slot` is a canonical 0-based index, not the stored `position` — see `resolveCostingInfoLabel`.
   */
  const getCostingInfoLabel = (slot: number, fallback: string): string =>
    resolveCostingInfoLabel(defaultCostingInfo, slot, fallback, isCostingInfoActive);

  /** Reads a dynamic costingInfo value off the row's template snapshot. */
  const getDynamicCostingValue = (cost: CostingRecord | undefined, item: CostingInfoField): string => {
    const snap = cost?.productTemplate?.costingInfo?.find((ci) => String(ci?._id) === String(item?._id));
    const value = snap?.value;
    if (value === undefined || value === null || value === '') return '--';
    if (item?.type === 'datePicker') {
      const parsedDate = moment(value);
      return parsedDate.isValid() ? parsedDate.format('DD-MM-YY') : '--';
    }
    return String(value);
  };

  /* Rows without expandable data are padded in as `null`. The padding is load-bearing: the
     expand-all chevron compares raw lengths against costingData (see B3). */
  const expandAll = () => {
    if (expandedRowIndex?.length > 0 && expandedRowIndex?.length === initState?.costingData?.length) {
      setExpandedRowIndex([]);
    } else {
      setExpandedRowIndex(initState?.costingData?.map((item, index) => ((item?.expandedData?.length ?? 0) > 1 ? index : null)));
    }
  };

  const rowData = useMemo<CostingGridRow[]>(() => {
    const rows: CostingGridRow[] = [];
    (initState?.costingData || []).forEach((cost, i) => {
      const hasExpanded = (cost?.expandedData?.length ?? 0) > 1;
      const rowOneData = cost?.expandedData?.[0];
      rows.push({ __cagId: cost?._id as string, __rowType: 'parent', parentIndex: i, cost, hasExpanded, rowOneData, __showChevron: hasExpanded });
      if (expandedRowIndex?.includes(i)) {
        (cost?.expandedData || []).forEach((expData, j) => {
          rows.push({
            __cagId: `${cost?._id}_${j}`,
            __rowType: 'child',
            parentIndex: i,
            cost,
            expData,
            landedInforData: cloneDeep(expData?.landedData?.landedInfo),
          });
        });
      }
    });
    return rows;
  }, [initState?.costingData, expandedRowIndex]);

  /** Re-applies the redux selection to the grid so it survives paging and expand/collapse. */
  const reconcileSelection = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;
    isSyncingSelectionRef.current = true;
    api.forEachNode((node: IRowNode<CostingGridRow>) => {
      if (node?.data?.__rowType === 'parent') {
        const id = node.data.cost?._id;
        const shouldSelect = (!!id && costingSelectedIds?.includes(id)) || false;
        if (node.isSelected() !== shouldSelect) node.setSelected(shouldSelect);
      }
    });
    isSyncingSelectionRef.current = false;
  }, [costingSelectedIds]);

  useEffect(() => {
    reconcileSelection();
  }, [reconcileSelection, rowData]);

  const onGridReady = (event: GridReadyEvent<CostingGridRow>) => {
    gridApiRef.current = event.api;
    reconcileSelection();
  };

  const onSelectionChanged = (event: SelectionChangedEvent<CostingGridRow>) => {
    if (isSyncingSelectionRef.current) return;
    const rows = event.api.getSelectedRows();
    const ids = rows.map((r) => r.cost?._id).filter(Boolean) as string[];
    dispatch(setCostingSelectedIds(ids));
    if (getSelectedCount) getSelectedCount(ids);
    if (getSelectedIdsWithDevIds) {
      getSelectedIdsWithDevIds(rows.map((r) => ({ costingId: r.cost?._id, devOrderId: r.cost?.devOrderId })).filter((x) => x.costingId));
    }
  };

  const canSelect = !isSupplier && (MasterAddEditAccess || MasterDeleteAccess);

  /* Read through a ref so the gridOptions memo does not rebuild on every paging flag flip. */
  const scrollGateRef = useRef({ hasMore: true, loadingMore: false });
  scrollGateRef.current = { hasMore: initState.hasMore, loadingMore: initState.loading };
  const handlePageNextInScrollRef = useRef(handlePageNextInScroll);
  handlePageNextInScrollRef.current = handlePageNextInScroll;

  /**
   * Restores the continuous prefetch BombiixAGGrid drops. Its own onBodyScrollEnd stays in
   * place as a second trigger; both are gated by the synchronous in-flight ref.
   */
  const onBodyScroll = useCallback((params: BodyScrollEvent<CostingGridRow>) => {
    if (params.direction === 'horizontal') return;
    const { hasMore, loadingMore } = scrollGateRef.current;
    if (!hasMore || loadingMore) return;
    const range = params.api.getVerticalPixelRange();
    if (!range) return;
    const contentHeight = params.api.getDisplayedRowCount() * GRID_ROW_HEIGHT;
    if (contentHeight <= 0) return;
    const viewportHeight = range.bottom - range.top;
    const contentFillsViewport = contentHeight > viewportHeight;
    const passedThreshold = range.bottom >= contentHeight * LOAD_MORE_SCROLL_RATIO;
    if (passedThreshold && (range.top > 0 || !contentFillsViewport)) handlePageNextInScrollRef.current();
  }, []);

  const gridOptions = useMemo<GridOptions<CostingGridRow>>(
    () => ({
      ...(canSelect
        ? {
            rowSelection: {
              mode: 'multiRow' as const,
              checkboxes: (p: { data?: CostingGridRow | undefined }) => p.data?.__rowType === 'parent',
              headerCheckbox: true,
              enableClickSelection: false,
              isRowSelectable: (n: IRowNode<CostingGridRow>) => n.data?.__rowType === 'parent',
            },
            selectionColumnDef: { pinned: 'left' as const, width: 56, suppressHeaderMenuButton: true, suppressMovable: true },
          }
        : {}),
      onGridReady,
      onSelectionChanged,
      onBodyScroll,
      /* BombiixAGGrid hardcodes suppressColumnVirtualisation; with ~60 dynamic columns that
         would render every cell of every buffered row on each rowData change. */
      suppressColumnVirtualisation: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSelect, onBodyScroll],
  );

  const editOptions = useCostingTodoOptions(initState.costingData);

  /**
   * Repaints the edited costings from the server's recomputed figures.
   * The response is authoritative for every derived column, so it replaces the locally echoed
   * inputs rather than merging with them.
   */
  const handleCalculated = useCallback((projections: CostingCalcProjection[]) => {
    const byId = new Map(projections.map((projection) => [projection._id, projection]));
    setInitState((state) => ({
      ...state,
      costingData: state.costingData.map((costing) => {
        const projection = costing?._id ? byId.get(costing._id) : undefined;
        return projection ? applyCalcProjection(costing, projection) : costing;
      }),
    }));
  }, []);

  const { queueEdit, scheduleFlush, isSaving } = useCostingSave(handleCalculated, undefined, onCostingsSaved);
  /* Editing waits on any save already in flight, so a second edit cannot be sent against figures the
     database has not accepted yet. The calculation lookups no longer gate it — they live on the
     server now, which is why there is nothing to wait for before the first edit. */
  const editGates = useMemo(() => ({ canEdit: MasterAddEditAccess && !isSaving, isSupplierLoggedIn: !!isSupplierLoggedIn }), [MasterAddEditAccess, isSaving, isSupplierLoggedIn]);

  /* Aggregate chips above the column headers. Computed from the costing-stats endpoint, which
     aggregates the same first-distribution-centre rows the grid renders, so the chip reconciles
     with the figures below it. */
  const headerStats = useMemo(
    () => buildCostingHeaderStats(averageTotalCal, { selectedColumns, topColumnsArr, defaultColumnsArr, bottomColumnsArr }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [averageTotalCal, selectedColumns?.length, topColumnsArr, defaultColumnsArr, bottomColumnsArr],
  );

  const columnDefs = useCostingTodoColumns({
    selectedColumns,
    t,
    tMyTodo,
    getCostingInfoLabel,
    getDynamicCostingValue,
    onSortColumn,
    sortOrder,
    sortPropertyName,
    expandedRowIndex,
    expandMoreData,
    expandAll,
    costingData: initState.costingData,
    isCostingInfoActive,
    dynamicCostingInfo,
    topColumnsArr,
    defaultColumnsArr,
    bottomColumnsArr,
    activeCostingTemplateDetailes,
    isSupplierLoggedIn: !!isSupplierLoggedIn,
    MasterAddEditAccess,
    subBrandAccess: !!subBrandAccess,
    subBrandList,
    handleEdit,
    editGates,
    editOptions,
  });

  const getGridRowId = useCallback((row: CostingGridRow) => row.__cagId, []);
  /** Row styling: the expand indent, plus a save marker while an inline edit is in flight. */
  const getGridRowClass = useCallback((row?: CostingGridRow) => (row?.__rowType === 'child' ? ['expand-row'] : []), []);

  /**
   * Records one inline cell edit: queues it for the server and echoes the typed value locally so the
   * input holds what was entered until the response lands. Nothing is calculated here — every
   * derived column repaints from `handleCalculated` once the server has recomputed the costing.
   */
  const handleRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const row = rowData.find((entry) => entry.__cagId === rowId);
      const costingId = row?.cost?._id;
      if (!row || !costingId) return;

      const column = columnDefs.find((definition) => definition.colId === field);
      const columnProps = column?.cellProps as { dynamicColumnId?: string; dynamicColumnKey?: string } | undefined;
      const dynamicColumn = columnProps?.dynamicColumnId ? { columnId: columnProps.dynamicColumnId, columnKey: columnProps.dynamicColumnKey ?? '' } : undefined;

      const edit = buildCostingEdit(field, value, row, dynamicColumn);
      if (!edit) return;

      setInitState((state) => ({
        ...state,
        costingData: state.costingData.map((costing) => (costing?._id === costingId ? applyLocalInput(costing as CostingRecord, edit, row) : costing)),
      }));
      queueEdit(costingId, edit);
      /* Choosing from a dropdown is a completed edit — there is no further typing to wait for, and
         focus often stays in the cell, so the blur that flushes text edits may never come. Flushing
         here is also what repaints the currency symbols after a supplier change: the re-based
         productInfo.currency only arrives on the calculate response. */
      if (column?.cellType === 'select') scheduleFlush();
    },
    [rowData, columnDefs, queueEdit, scheduleFlush],
  );

  /**
   * Save-on-blur. Any focus leaving a cell counts, including a move to the next cell, because the
   * cell being left has finished. The flush is deferred one tick past focusout so the cell's own
   * blur handler lands its final keystroke first.
   */
  const handleGridFocusOut = useCallback(() => scheduleFlush(), [scheduleFlush]);

  /** Builds the client-side workbook for the currently selected rows. */
  const downloadCostingTODOExcelSheet = async () => {
    if (!costingSelectedIds?.length) {
      console.error('No selected costing items for download');
      return;
    }

    const selectedCostingData = initState?.costingData?.filter((item) => costingSelectedIds.includes(item._id as string)) || [];

    if (!selectedCostingData?.length) {
      console.error('No costing data found for selected items');
      return;
    }

    await downloadCostingTodoExcelSheet(selectedCostingData, {
      selectedColumns,
      topColumnsArr,
      defaultColumnsArr,
      bottomColumnsArr,
      activeCostingTemplateDetailes,
    });
  };

  /**
   * Commits a fill-handle drag or paste: every accepted cell is echoed in one state update and
   * queued, then flushed once. A drag never blurs a cell, so the save-on-blur path would not run.
   * The state update always hands back a new array, which rebuilds the rows and drops the values
   * AG Grid wrote onto them for any cell that was refused.
   */
  const handleFillCells = useCallback(
    (batch: BombiixFillBatchItem<CostingGridRow>[]) => {
      const fillEdits = buildFillEdits(batch, columnDefs);
      setInitState((state) => {
        const costings = new Map<string, CostingRecord>();
        state.costingData.forEach((costing) => {
          if (costing?._id) costings.set(costing._id, costing as CostingRecord);
        });
        fillEdits.forEach(({ costingId, edit, row }) => {
          const current = costings.get(costingId);
          if (current) costings.set(costingId, applyLocalInput(current, edit, row));
        });
        return { ...state, costingData: state.costingData.map((costing) => (costing?._id ? (costings.get(costing._id) ?? costing) : costing)) };
      });
      fillEdits.forEach(({ costingId, edit }) => queueEdit(costingId, edit));
      if (fillEdits.length) scheduleFlush();
    },
    [columnDefs, queueEdit, scheduleFlush],
  );

  return (
    <>
      {/** 16px is the designed gap between the template tabs above and the stat-chip band that
           opens this grid — the band is the first thing painted here, so this padding IS that gap. */}
      <div id="costingTableContainer" className="pt-16">
        {initState?.costingData?.length > 0 ? (
          <div className="costing-todo-grid" onBlur={handleGridFocusOut}>
            <BombiixAGGrid<CostingGridRow>
              fullWidthHorizontalScroll
              fullWidthHorizontalScrollEdgeRight
              revealHeaderControlsOnHover
              showColumnChooser
              enableActiveCellNavigation
              columnDefs={columnDefs}
              rowData={rowData}
              getRowId={getGridRowId}
              getRowClass={getGridRowClass}
              onRowChange={handleRowChange}
              enableFillHandle
              onFillCells={handleFillCells}
              height="calc(100vh - 290px)"
              hasMore={initState.hasMore}
              loadingMore={initState.loading}
              onLoadMore={handlePageNextInScroll}
              currencySymbol=""
              headerStats={headerStats}
              gridOptions={gridOptions}
            />
            {initState.loading && (
              <div className="costing-todo-grid__loading" aria-live="polite" aria-busy="true">
                <Loader />
              </div>
            )}
          </div>
        ) : (
          initState?.costingData?.length === 0 && <p className="text-center">{t('COSTING_TO_DO_LIST.NO_DATA_FOUND')}</p>
        )}
      </div>
      {MasterAddEditAccess && (
        <Floaty position="fixed">
          <FloatyButton
            icon={DownloadIcon}
            label={t('COMMON_LABEL.DOWNLOAD')}
            onClick={downloadCostingTODOExcelSheet}
            disabled={isDownloadLoading || costingSelectedIds?.length === 0}
          />
        </Floaty>
      )}
    </>
  );
}

export default CostingTODOTable;
